import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SubscriptionChecklist from './SubscriptionChecklist';
import {
    getClientSubscriptions,
    getDocumentGroups,
    getSubscribableDocuments,
    getSubscribableRoles,
    PRECONDITION_FAILED,
    saveClientSubscriptions,
} from './Transactions';

// RoleType.Solicitor in iprs-api
const SOLICITOR_ROLE_TYPE_ID = 2010;

const isSolicitorRole = (role) => role.roleTypeID === SOLICITOR_ROLE_TYPE_ID;

// The documents a role may have: solicitors only get the solicitor ("S") ones; referrers
// can have any, solicitor ones included
const documentsForRole = (documents, role) =>
    (isSolicitorRole(role) ? documents.filter((d) => d.isSolicitorDocument) : documents);

// Solicitors only have the four S documents, so they get Select all / none rather than
// a box with one group in it
const groupsForRole = (groups, role) => (isSolicitorRole(role) ? [] : groups);

// The documents shown for a role. Where the role has groups (referrers), documents in none
// of them are hidden, and so is the "Other Reports" group checkbox, which then has nothing
// in it. The API groups every document a triage or eForm report sends to subscribers, so
// the ungrouped ones would never be sent anyway. This may be a temporary fix: the API could
// stop offering ungrouped documents instead. Hidden documents a role is already subscribed
// to stay subscribed: they're kept in the selection and sent on Save, just not shown.
const visibleDocumentsForRole = (documents, groups, role) => {
    const roleDocuments = documentsForRole(documents, role);
    const roleGroups = groupsForRole(groups, role);
    if (roleGroups.length === 0) {
        return roleDocuments;
    }
    const shownGroups = new Set(roleGroups.map((g) => g.group));
    return roleDocuments.filter((d) => shownGroups.has(d.group));
};

/** The saved, allowed, active documents per role, as { roleTypeID: Set }. */
const selectionFromSaved = (roles, documents, subscriptions) => Object.fromEntries(roles.map((role) => {
    const allowed = new Set(documentsForRole(documents, role).map((d) => d.documentID));
    const ids = subscriptions
        .filter((s) => s.roleTypeID === role.roleTypeID && s.active && allowed.has(s.documentID))
        .map((s) => s.documentID);
    return [role.roleTypeID, new Set(ids)];
}));

const sameSet = (a, b) => a.size === b.size && [...a].every((id) => b.has(id));

/**
 * Per-client configuration of the documents new referrers and solicitors are
 * automatically subscribed to (#3814). Used on the Pharos Organisation Management
 * "Report Subscriptions" tab and in the dev harness.
 *
 * Ticking boxes only changes the form; Save sends every role's full list in one PUT.
 * It isn't part of Pharos's "Save Client" button. The PUT carries the ETag from loading
 * (If-Match), so if someone else saved in the meantime it's refused rather than
 * overwriting their change, and the user is asked to reload.
 */
const ClientDocumentSubscriptions = ({ api, clientID }) => {
    const [roles, setRoles] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [groups, setGroups] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [etag, setETag] = useState(null);
    const [selection, setSelection] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [conflict, setConflict] = useState(false);
    const [status, setStatus] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        setConflict(false);
        setStatus('');
        const [rolesResult, documentsResult, groupsResult, subscriptionsResult] = await Promise.all([
            getSubscribableRoles(api),
            getSubscribableDocuments(api),
            getDocumentGroups(api),
            getClientSubscriptions(api, clientID),
        ]);
        const firstError = rolesResult.error || documentsResult.error
            || groupsResult.error || subscriptionsResult.error;
        if (firstError) {
            setError(firstError.message);
        } else {
            setRoles(rolesResult.data);
            setDocuments(documentsResult.data);
            setGroups(groupsResult.data);
            setSubscriptions(subscriptionsResult.data);
            setETag(subscriptionsResult.etag);
            setSelection(selectionFromSaved(rolesResult.data, documentsResult.data, subscriptionsResult.data));
        }
        setLoading(false);
    }, [api, clientID]);

    useEffect(() => {
        if (clientID) {
            load();
        }
    }, [clientID, load]);

    const saved = useMemo(
        () => selectionFromSaved(roles, documents, subscriptions),
        [roles, documents, subscriptions]);

    const notAllowedByRole = useMemo(() => Object.fromEntries(roles.map((role) => {
        const allowed = new Set(documentsForRole(documents, role).map((d) => d.documentID));
        return [role.roleTypeID, subscriptions.filter((s) =>
            s.roleTypeID === role.roleTypeID && !allowed.has(s.documentID))];
    })), [roles, documents, subscriptions]);

    const hasNotAllowed = Object.values(notAllowedByRole).some((rows) => rows.length > 0);
    const dirty = roles.some((role) =>
        !sameSet(selection[role.roleTypeID] || new Set(), saved[role.roleTypeID] || new Set()));

    // Warn before a Pharos postback or navigation throws away unsaved ticks
    useEffect(() => {
        if (!dirty) {
            return undefined;
        }
        const warn = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirty]);

    const handleChange = (roleTypeID, documentIDs, checked) => {
        setStatus('');
        setSelection((current) => {
            const next = new Set(current[roleTypeID]);
            documentIDs.forEach((id) => (checked ? next.add(id) : next.delete(id)));
            return { ...current, [roleTypeID]: next };
        });
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setConflict(false);
        setStatus('');
        const result = await saveClientSubscriptions(api, clientID, roles.map((role) => ({
            roleTypeID: role.roleTypeID,
            documentIDs: [...(selection[role.roleTypeID] || [])].sort((a, b) => a - b),
        })), etag);
        if (result.error && result.error.status === PRECONDITION_FAILED) {
            setConflict(true);
        } else if (result.error) {
            setError(result.error.message);
        } else {
            setSubscriptions(result.data);
            setETag(result.etag);
            setSelection(selectionFromSaved(roles, documents, result.data));
            setStatus('Saved.');
        }
        setSaving(false);
    };

    const handleCancel = () => {
        setError('');
        setStatus('');
        setSelection(saved);
    };

    if (!clientID) {
        return <p>No client selected.</p>;
    }

    if (loading) {
        return <p>Loading report subscriptions…</p>;
    }

    return (
        <div className="client-document-subscriptions" style={{ padding: '10px' }}>
            <p>
                Choose the reports that referrers and solicitors of this client are subscribed
                to <strong>when they are first given that role</strong>. Changing this list
                doesn&apos;t change anyone&apos;s existing subscriptions; edit those on the user&apos;s
                Profile &gt; Reports tab.
            </p>
            <p>
                Press Save below to keep your changes. This isn&apos;t part of the Save Client
                button, and changes appear on the Audit Log tab after the page is next loaded.
            </p>

            {error && (
                <div role="alert" style={{ color: 'red', marginBottom: '10px' }}>
                    {error}
                </div>
            )}

            {conflict && (
                <div role="alert" style={{ color: 'red', marginBottom: '10px' }}>
                    Someone else has changed these report subscriptions since you opened them, so
                    yours weren&apos;t saved. Reload to see their changes, then make yours again.
                    {' '}
                    <button type="button" className="btn btn-sm btn-default" onClick={load}>
                        Reload
                    </button>
                </div>
            )}

            {roles.map((role) => (
                <SubscriptionChecklist
                    key={role.roleTypeID}
                    role={role}
                    documents={visibleDocumentsForRole(documents, groups, role)}
                    groups={groupsForRole(groups, role)}
                    selectedIDs={selection[role.roleTypeID] || new Set()}
                    notAllowed={notAllowedByRole[role.roleTypeID] || []}
                    disabled={saving}
                    onChange={(documentIDs, checked) => handleChange(role.roleTypeID, documentIDs, checked)}
                />
            ))}

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {/* type="button" so these never submit the Pharos form around the widget */}
                <button
                    type="button"
                    className="btn btn-primary"
                    disabled={saving || conflict || (!dirty && !hasNotAllowed)}
                    onClick={handleSave}
                >
                    {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                    type="button"
                    className="btn btn-default"
                    disabled={saving || !dirty}
                    onClick={handleCancel}
                >
                    Cancel
                </button>
                <span role="status">{dirty ? 'You have unsaved changes.' : status}</span>
            </div>
        </div>
    );
};

export default ClientDocumentSubscriptions;
